/**
 * Interfaces and data types for multiplayer sessions and object replication.
 * Defines the IGameSession abstraction for client and server networking backends.
 */
import { Delegate } from '../utils/Delegate.js';

import type { SerializedData } from '../utils/serialization/types.js';

/**
 * Stable string ID for the local device (browser storage or generated in headless).
 */
export type DeviceId = string;

/**
 * Stable string ID for a connected game client (typically assigned by the
 * server during the handshake).
 */
export type ClientId = string;

/**
 * Actor network object identifier (format `ClassName'UUID'`).
 */
export type ObjectId = string;

/**
 * Composite network identity string (`DeviceId:ClientId:ObjectId`).
 */
export type NetId = string;

/**
 * Snapshot of bandwidth + latency stats for a single network connection.
 *
 * Implementations are expected to report wire-accurate byte counts when the
 * underlying transport exposes them (e.g. Node `ws` socket counters), and
 * fall back to application payload byte length otherwise.
 *
 * `rttMs` is sampled at the application layer via PING/PONG messages and
 * pushed into the connection via `recordRttSample()`. It is null until the
 * first PONG is received.
 *
 * Byte counts reflect application payload sizes (MessagePack bodies on the
 * multiplayer wire unless a transport overrides with TCP-level counters).
 */
export type ConnectionStatsSnapshot = {
  /** Total bytes sent on this connection since it was created */
  bytesSent: number;
  /** Total bytes received on this connection since it was created */
  bytesReceived: number;
  /** Total messages sent (one call to `send()` == one message) */
  messagesSent: number;
  /** Total messages received (one onMessage invocation == one message) */
  messagesReceived: number;
  /** Bytes sent per second over the most recent ~1s window */
  bytesSentPerSecond: number;
  /** Bytes received per second over the most recent ~1s window */
  bytesReceivedPerSecond: number;
  /** Last measured round-trip time in ms, or null if no PONG received yet */
  rttMs: number | null;
  /** Wall-clock time (ms since epoch) the connection was first opened */
  connectedAt: number;
  /** Implementation-specific extras (e.g. `bufferedAmount`, dropped frames) */
  extra?: Record<string, number>;
}

/**
 * Generic network connection interface used by game sessions.
 * Implementations may use WebSocket, WebTransport, or any future transport.
 *
 * Server-accepted connections are delivered in an already-open state, so
 * `connect()` should be treated as a no-op and `onOpen` is not guaranteed
 * to fire for them. Use `isOpen()` to check connection status.
 *
 * Implementations are responsible for tracking bandwidth (and any
 * transport-specific overhead they have visibility into) and exposing it
 * via `getStats()`. RTT is measured at the application layer via PING/PONG
 * messages and pushed into the connection via `recordRttSample()`.
 */
export interface INetworkConnection {
  readonly onOpen: Delegate<[], void>;
  readonly onMessage: Delegate<[Uint8Array], void>;
  readonly onClose: Delegate<[number, string], void>;
  readonly onError: Delegate<[Error], void>;

  isOpen(): boolean;
  connect(): void;
  send(data: Uint8Array): void;
  close(code?: number, reason?: string): void;

  /**
   * Get a snapshot of bandwidth + latency stats for this connection.
   * Cheap to call - implementations should avoid heavy computation.
   */
  getStats(): ConnectionStatsSnapshot;

  /**
   * Record a round-trip-time sample (in ms) measured by the application layer.
   * Typically called after receiving a PONG response to a PING.
   */
  recordRttSample(rttMs: number): void;
}

/**
 * Generic network server interface used by server-side game sessions.
 * Implementations may use WebSocket, WebTransport, or any future transport.
 *
 * The server listens for incoming client connections and emits each new
 * connection via `onConnection` as an INetworkConnection. Connections delivered
 * through this interface are already open and ready to send/receive data.
 */
export interface INetworkServer {
  readonly onConnection: Delegate<[INetworkConnection], void>;
  readonly onError: Delegate<[Error], void>;

  isListening(): boolean;
  /**
   * Start listening for connections on the given port.
   * Resolves once the server is bound and listening; rejects if the port
   * cannot be acquired (e.g. EADDRINUSE).
   */
  listen(port: number): Promise<void>;
  /**
   * Stop listening and close any active connections.
   */
  close(): Promise<void>;
}

/**
 * Game session lifecycle states
 */
export enum GameSessionState {
  /** Session is being initialized */
  Initializing = 'Initializing',
  /** Session is waiting for players to join */
  WaitingForPlayers = 'WaitingForPlayers',
  /** Session is starting (countdown, loading, etc.) */
  Starting = 'Starting',
  /** Session is actively running */
  InProgress = 'InProgress',
  /** Session is ending (post-game, cleanup) */
  Ending = 'Ending',
  /** Session is shut down and no longer accepting connections */
  Shutdown = 'Shutdown'
}

/**
 * Description of a game session
 */
export type GameSessionDescription = {
  gameId: string;
  sessionName: string | undefined;
  state?: GameSessionState;
  playerCount?: number;
  maxPlayers?: number;
  countdown?: number;
}

/**
 * State of each client
 */
export type ClientState = {
  lastHeartBeatTime: number;
}

/**
 * Delegate for when session state changes
 */
export type SessionStateChangedDelegate = Delegate<[GameSessionState, GameSessionState /** old state, new state */], void>;

/**
 * Per-Actor Replication Protocol
 * New message types for channel-based replication
 */

/**
 * Replication packet sent from server to client
 * Contains updates for multiple actors
 */
export interface ReplicationPacket {
  tick: number;
  actors: ActorUpdate[];
}

/**
 * Update for a single actor
 */
export type ActorUpdate =
  | { transportId: number; updateType: 'spawn'; data: SpawnData }
  | { transportId: number; updateType: 'update'; data: UpdateData }
  | { transportId: number; updateType: 'destroy' };

/**
 * Spawn data for a new actor
 */
export interface SpawnData {
  /**
   * Actor UUID for matching with scene-placed actors and RPC routing
   */
  uuid: string;
  /**
   * Construction data in SerializedData format for the Loader API.
   * Contains $version, $root with $base ($class or $path), and non-replicated properties only.
   */
  constructionData: SerializedData;
  owningClientId?: string;
  /**
   * Binary-encoded initial replicated state for the actor (and its components).
   *
   * NOTE: The server swaps netLocalRole and netRemoteRole before sending, so the client receives:
   * - netLocalRole: The role the client should use locally (e.g., AutonomousProxy or SimulatedProxy)
   * - netRemoteRole: The role the server has (typically Authority)
   */
  initialState?: Uint8Array;
}

/**
 * Update data for an existing actor containing only changed properties (delta).
 * The payload is a binary-encoded delta produced by {@link ReplicationSerializer.serializeActor}.
 *
 * `baseTick` identifies the tick whose snapshot the server used as the delta base.
 * The client must use `receivedSnapshots[baseTick]` as `prevState` when deserializing,
 * so that carry-forward of unchanged properties uses the same baseline as the server's diff.
 */
export interface UpdateData {
  payload: Uint8Array;
  /** The confirmed replication tick the server diffed from (server's `lastAckedTick`). */
  baseTick: number;
}

/**
 * Acknowledgment packet sent from client to server
 * Confirms receipt of actor updates
 */
export interface ReplicationAck {
  tick: number;
  actors: ActorAck[];
}

/**
 * Acknowledgment for a single actor
 */
export interface ActorAck {
  transportId: number;
  ackType: 'spawned' | 'updated';
}

/**
 * Reason a player disconnected from the session.
 */
export enum DisconnectReason {
  /** Client voluntarily left or lost connection */
  Voluntary = 'Voluntary',
  /** Server kicked the client due to timeout */
  Timeout = 'Timeout',
}

/**
 * Arbitrary metadata a client may supply when joining a session.
 * Sent as part of the handshake; passed through to `GameMode.canPlayerJoin()`
 * on the server side.
 */
export type JoinParams = Record<string, unknown>;

/**
 * Result returned by `GameMode.canPlayerJoin()`.
 */
export type PlayerJoinResult = { allowed: true } | { allowed: false; reason: string };

/**
 * Delegate for when a player joins the session
 */
export type PlayerJoinedDelegate = Delegate<[string /** clientId */, JoinParams], void>;

/**
 * Delegate for when a player leaves the session
 */
export type PlayerLeftDelegate = Delegate<[string /** clientId */], void>;

/**
 * Payload describing an incoming RPC delivered to listeners of
 * {@link IGameSession.onRPCReceived}.
 */
export interface IncomingRpc {
  objectId: ObjectId;
  methodName: string;
  /** Binary-encoded arguments; decode via RpcBinaryCodec.decodeRpcArgs. */
  argsPayload: Uint8Array;
  /** See {@link RpcMessage.netSubId}. */
  netSubId?: string;
}

/**
 * Interface for a game session
 */
export abstract class IGameSession {
  /** Fires when session state transitions (e.g. WaitingForPlayers → InProgress). */
  public readonly onSessionStateChanged: SessionStateChangedDelegate = new Delegate<[GameSessionState, GameSessionState], void>();
  /** Fires when a player joins the session. */
  public readonly onPlayerJoined: PlayerJoinedDelegate = new Delegate<[string, JoinParams], void>();
  /** Fires when a player leaves the session. */
  public readonly onPlayerLeft: PlayerLeftDelegate = new Delegate<[string], void>();
  /** Fires when an RPC is received from the peer. */
  public readonly onRPCReceived: Delegate<[IncomingRpc, ClientId?], void> = new Delegate();
  /** Fires when a replication packet is received from the server (client-side). */
  public readonly onReplicationPacketReceived: Delegate<[ReplicationPacket], void> = new Delegate();
  /** Fires when a replication ack is received from a client (server-side). */
  public readonly onReplicationAckReceived: Delegate<[ReplicationAck, ClientId], void> = new Delegate();

  protected state: GameSessionState = GameSessionState.Initializing;
  protected playerCount: number = 0;
  protected maxPlayers: number = 0;
  protected countdown: number | undefined = undefined;

  constructor(protected description: GameSessionDescription) {
    // Initialize state from description if provided
    if (description.state !== undefined) {
      this.state = description.state;
    }
    if (description.playerCount !== undefined) {
      this.playerCount = description.playerCount;
    }
    if (description.maxPlayers !== undefined) {
      this.maxPlayers = description.maxPlayers;
    }
    if (description.countdown !== undefined) {
      this.countdown = description.countdown;
    }
  }

  /**
   * Get the description of the game session
   */
  public getDescription(): GameSessionDescription {
    return {
      ...this.description,
      state: this.state,
      playerCount: this.playerCount,
      maxPlayers: this.maxPlayers,
      countdown: this.countdown
    };
  }

  /**
   * Get the current session state
   */
  public getState(): GameSessionState {
    return this.state;
  }

  /**
   * Set the session state on this process. Server builds replicate session fields
   * to clients via `GameSessionInfo`.
   */
  public setState(newState: GameSessionState): void {
    const oldState = this.state;
    if (oldState !== newState) {
      this.state = newState;
      this.description.state = newState;
      this.onSessionStateChanged.invoke(oldState, newState);
    }
  }

  /**
   * Get the current player count
   */
  public getPlayerCount(): number {
    return this.playerCount;
  }

  /**
   * Set the player count
   */
  public setPlayerCount(count: number): void {
    this.playerCount = count;
    this.description.playerCount = count;
  }

  /**
   * Get the maximum number of players
   */
  public getMaxPlayers(): number {
    return this.maxPlayers;
  }

  /**
   * Set the maximum number of players
   */
  public setMaxPlayers(max: number): void {
    this.maxPlayers = max;
    this.description.maxPlayers = max;
  }

  /**
   * Get the countdown value (if any)
   */
  public getCountdown(): number | undefined {
    return this.countdown;
  }

  /**
   * Set the countdown value
   */
  public setCountdown(countdown: number | undefined): void {
    this.countdown = countdown;
    this.description.countdown = countdown;
  }

  /**
   * Handle a player joining the session
   */
  public handlePlayerJoined(clientId: ClientId, joinParams: JoinParams = {}): void {
    this.playerCount++;
    this.description.playerCount = this.playerCount;
    this.onPlayerJoined.invoke(clientId, joinParams);
  }

  /**
   * Handle a player leaving the session
   */
  public handlePlayerLeft(clientId: ClientId): void {
    this.playerCount = Math.max(0, this.playerCount - 1);
    this.description.playerCount = this.playerCount;
    this.onPlayerLeft.invoke(clientId);
  }

  /**
   * Client heartbeat
   */
  public abstract clientHeartBeat(clientId: ClientId): Promise<boolean>;

  /**
   * Get all clients
   */
  public abstract getClients(): Promise<Record<ClientId, ClientState>>;

  /**
   * Tear down the session and release all resources (sockets, timers, etc.).
   * Called when disconnecting from the server or shutting down the server.
   */
  public abstract destroy(): void;

  //==================================================================
  // Transport hooks
  //
  // The next block of methods describes operations exposed by concrete
  // session implementations (client- or server-side). The base class
  // ships sensible defaults so code in `NetWorld` can call into the
  // session without `instanceof` checks; concrete subclasses override
  // the ones that apply to their role.
  //==================================================================

  /**
   * Server-side: send an RPC to one or more clients.
   * Default implementation is a no-op (client sessions cannot push to other clients).
   *
   * @param objectId      The NetObjectId of the actor to invoke the method on.
   * @param methodName    Name of the RPC method.
   * @param argsPayload   Binary-encoded arguments (from {@link encodeRpcArgs}).
   * @param reliable      Whether to use a reliable ordered channel (default `true`).
   * @param targetClients List of client IDs to send to, or `null` for all clients.
   * @param netSubId      Optional component sub-identifier for component RPC routing.
   */
  public sendClientRpc(
    _objectId: ObjectId,
    _methodName: string,
    _argsPayload: Uint8Array,
    _reliable: boolean,
    _targetClients: ClientId[] | null,
    _netSubId?: string
  ): void {
    console.warn(`[${this.constructor.name}] sendClientRpc is not supported on this session.`);
  }

  /**
   * Server-side: deliver a replication packet to a specific client.
   * Default implementation is a no-op.
   */
  public sendReplicationPacket(_clientId: ClientId, _packet: ReplicationPacket): void {
    console.warn(`[${this.constructor.name}] sendReplicationPacket is not supported on this session.`);
  }

  /**
   * Server-side: start listening for client connections on the given port range.
   * Default implementation throws — only server sessions can host clients.
   *
   * @returns The port that was successfully bound.
   */
  public async startListening(_portRange: [number, number]): Promise<number> {
    throw new Error(`[${this.constructor.name}] startListening is not supported on this session.`);
  }

  /**
   * Server-side: per-client connection stats keyed by client ID.
   * Default implementation returns an empty record (clients have a single connection).
   */
  public getClientStats(): Record<ClientId, ConnectionStatsSnapshot> {
    return {};
  }

  /**
   * Client-side: send an RPC to the server.
   * Default implementation is a no-op (server sessions cannot call themselves).
   *
   * @param objectId    The NetObjectId of the actor to invoke the method on.
   * @param methodName  Name of the RPC method.
   * @param argsPayload Binary-encoded arguments (from {@link encodeRpcArgs}).
   * @param reliable    Whether to use a reliable ordered channel (default `true`).
   * @param netSubId    Optional component sub-identifier for component RPC routing.
   */
  public sendServerRpc(
    _objectId: ObjectId,
    _methodName: string,
    _argsPayload: Uint8Array,
    _reliable: boolean,
    _netSubId?: string
  ): void {
    console.warn(`[${this.constructor.name}] sendServerRpc is not supported on this session.`);
  }

  /**
   * Client-side: send a replication acknowledgment back to the server.
   * Default implementation is a no-op.
   */
  public sendReplicationAck(_ack: ReplicationAck): void {
    console.warn(`[${this.constructor.name}] sendReplicationAck is not supported on this session.`);
  }

  /**
   * Client-side: wait until the connection is open and the handshake has completed.
   * Default implementation resolves immediately (server sessions are ready as soon
   * as they have started listening).
   */
  public async waitUntilReady(): Promise<void> {
    // No-op by default.
  }

  /**
   * Client-side: return the connection-level stats snapshot, or `null` if not
   * applicable to this session (e.g. on the server).
   */
  public getConnectionStats(): ConnectionStatsSnapshot | null {
    return null;
  }

  /**
   * Client-side: the client ID assigned by the server during handshake, or
   * `null` if not yet known (or not applicable).
   */
  public getAssignedClientId(): ClientId | null {
    return null;
  }
}
